import React, { useState, useCallback, useRef } from 'react'
import _ from 'lodash'
import type { SeekQueryResult, Maybe, Listing } from '@adc/sdk/types'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import { searchListings, ListingSearchFieldFragment } from './searchListings'
import SearchField, { type ISearchFieldProps } from '../searchField'
import ListingItem from './listingItem'
import classnames from 'classnames/bind'
import styles from './styles.scss'

// @ts-ignore unsafe assignment of any
const cx = classnames.bind(styles)

const DEBOUNCE_WAIT_FOR_SEARCH_TYPING_MS = 500

type ListingResult = SeekQueryResult & {
  listing_id: string
}

type Selection = {
  key: string
  title: string
  description: string
  renderer: () => JSX.Element
  listing: ListingResult
}

export interface IListingSearchFieldProps extends Partial<ISearchFieldProps> {
  placeholder?: string
  onChange: (value: string) => void
  onChangeCurrentListing: (listing: Maybe<Partial<Listing>>) => void
  currentListing?: Maybe<Partial<Listing>>
  onBlur?: () => void
  value: string
  viewMode?: boolean
}

interface ListingSearchFieldType extends React.FC<IListingSearchFieldProps> {
  [GRAPH_API_FRAGMENT_DEFINITION]: string
  [GRAPH_API_FRAGMENT_REFERENCE]: string
}

/* eslint-disable react/prop-types */
// @ts-ignore next line
const ListingSearchField: ListingSearchFieldType = ({
  onChange,
  onBlur, // eslint-disable-line @typescript-eslint/no-unused-vars -- we don't want this to fire...
  placeholder = 'Search by Listing Id (in quotes) or by address',
  viewMode,
  // initialListing,
  onChangeCurrentListing,
  currentListing,
  ...rest
}) => {
  const [results, setResults] = useState<ListingResult[]>([])
  const [loading, setLoading] = useState(false)
  const fetchCounter = useRef(0)

  const fetchListings = useCallback(
    async (searchTerm: string, currentFetchId: number) => {
      const results = await searchListings(searchTerm)
      // only update our state if this is the most recent fetch
      if (currentFetchId === fetchCounter.current) {
        // @ts-ignore ts is worried that some content items could be null... heck with that.
        const content: ListingResult[] = results?.data?.content || []
        setResults(content)
      }
    },
    []
  )

  const debouncedFetchListings = useRef(
    _.debounce(async (value: string) => {
      setLoading(true)
      const currentFetchId = ++fetchCounter.current
      await fetchListings(value, currentFetchId)
      setLoading(false)
    }, DEBOUNCE_WAIT_FOR_SEARCH_TYPING_MS)
  ).current

  const onSearchChange = useCallback<
    NonNullable<ISearchFieldProps['onSearchChange']>
  >(
    async (_e, { value }) => {
      await debouncedFetchListings(value as string)
    },
    [debouncedFetchListings]
  )

  const resultRenderer = (listing: ListingResult) => {
    return (
      <ListingItem
        elmId='testing'
        listing={listing as Maybe<Partial<Listing>>}
      />
    )
  }

  const onResultSelect = (_e: unknown, data: { result: Selection }) => {
    onChangeCurrentListing(data.result.listing as Maybe<Partial<Listing>>)
    onChange(data.result.key || '') // this is for anyone plugged into onChange
  }

  const resultsMapped = results.map(result => ({
    key: result?.listing_id,
    title: result?.listing_id,
    description: result?.listing_id,
    renderer: () => resultRenderer(result),
    listing: result,
  }))

  return (
    <div
      className={cx('container', {
        expanded: currentListing,
        'has-label': !!rest.label,
      })}
    >
      <SearchField
        results={resultsMapped}
        onSearchChange={onSearchChange}
        onResultSelect={onResultSelect}
        noResultsMessage='No results found'
        selectFirstResult
        showNoResults
        className={cx('listing-search-field')}
        loading={loading}
        placeholder={placeholder}
        viewMode={viewMode}
        borderBottom='none'
        onChange={onChange}
        {...rest}
      />
      <div
        className={cx({
          'listing-item-cover-mode': currentListing,
          'has-label': !!rest.label,
        })}
      >
        <ListingItem
          elmId='listing_item'
          listing={currentListing || null}
          onClose={() => {
            onChangeCurrentListing(null)
            onChange('')
          }}
          viewMode={viewMode}
        />
      </div>
    </div>
  )
}

ListingSearchField[GRAPH_API_FRAGMENT_DEFINITION] = ListingSearchFieldFragment
ListingSearchField[GRAPH_API_FRAGMENT_REFERENCE] = `...ListingSearchFields`
export default ListingSearchField
